import React, { useState, useEffect } from 'react';
import { COLORS, SPACING, TYPOGRAPHY, RADIUS } from '../design/tokens';
import { Modal, Button, Input, Checkbox } from '../design/primitives';
import { apiClient } from '../api/apiConfig';
import { EmailRule, EmailRuleConditions, EmailRuleActions } from '../types/email';

interface EmailRuleManagerProps {
  isOpen: boolean;
  onClose: () => void;
}

const EMPTY_CONDITIONS: EmailRuleConditions = {
  from_contains: '',
  from_domain: '',
  to_contains: '',
  subject_contains: '',
};

const EMPTY_ACTIONS: EmailRuleActions = {
  archive: false,
  mark_read: false,
  star: false,
  mark_handled: false,
  mark_newsletter: false,
  assign_category_id: null,
  assign_business_id: null,
  assign_project_id: null,
  assign_person_id: null,
};

const EmailRuleManager: React.FC<EmailRuleManagerProps> = ({ isOpen, onClose }) => {
  const [rules, setRules] = useState<EmailRule[]>([]);
  const [editing, setEditing] = useState<EmailRule | null>(null);
  const [creating, setCreating] = useState(false);
  const [loading, setLoading] = useState(false);
  const [testResult, setTestResult] = useState<{ match_count: number; total_tested: number; samples: any[] } | null>(null);

  // Form state
  const [name, setName] = useState('');
  const [priority, setPriority] = useState(0);
  const [conditions, setConditions] = useState<EmailRuleConditions>({ ...EMPTY_CONDITIONS });
  const [actions, setActions] = useState<EmailRuleActions>({ ...EMPTY_ACTIONS });

  // Dropdown options
  const [categories, setCategories] = useState<Array<{ id: number; name: string }>>([]);
  const [businesses, setBusinesses] = useState<Array<{ id: number; name: string }>>([]);
  const [projects, setProjects] = useState<Array<{ id: number; name: string }>>([]);
  const [people, setPeople] = useState<Array<{ id: number; name: string }>>([]);

  const fetchRules = async () => {
    try {
      const res = await apiClient.get('/api/email-rules/');
      setRules(res.data || []);
    } catch (err) {
      console.error('Failed to fetch rules:', err);
    }
  };

  const fetchDropdownData = async () => {
    try {
      const [catRes, bizRes, projRes, pplRes] = await Promise.all([
        apiClient.get('/api/categories/'),
        apiClient.get('/api/businesses/'),
        apiClient.get('/api/projects/'),
        apiClient.get('/api/people/'),
      ]);
      setCategories(catRes.data || []);
      setBusinesses(bizRes.data || []);
      setProjects(projRes.data || []);
      setPeople(pplRes.data || []);
    } catch (err) {
      console.error('Failed to fetch dropdown data:', err);
    }
  };

  useEffect(() => {
    if (isOpen) {
      fetchRules();
      fetchDropdownData();
    }
  }, [isOpen]);

  const resetForm = () => {
    setName('');
    setPriority(0);
    setConditions({ ...EMPTY_CONDITIONS });
    setActions({ ...EMPTY_ACTIONS });
    setEditing(null);
    setCreating(false);
    setTestResult(null);
  };

  const openEdit = (rule: EmailRule) => {
    setEditing(rule);
    setCreating(false);
    setName(rule.name);
    setPriority(rule.priority);
    setConditions({ ...EMPTY_CONDITIONS, ...rule.conditions });
    setActions({ ...EMPTY_ACTIONS, ...rule.actions });
    setTestResult(null);
  };

  const openCreate = () => {
    resetForm();
    setCreating(true);
  };

  const handleSave = async () => {
    setLoading(true);
    try {
      const payload = { name, priority, conditions, actions, is_active: true };
      if (editing) {
        await apiClient.put(`/api/email-rules/${editing.id}/`, payload);
      } else {
        await apiClient.post('/api/email-rules/', payload);
      }
      await fetchRules();
      resetForm();
    } catch (err) {
      console.error('Failed to save rule:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id: number) => {
    try {
      await apiClient.delete(`/api/email-rules/${id}/`);
      await fetchRules();
      if (editing?.id === id) resetForm();
    } catch (err) {
      console.error('Failed to delete rule:', err);
    }
  };

  const handleToggleActive = async (rule: EmailRule) => {
    try {
      await apiClient.patch(`/api/email-rules/${rule.id}/`, { is_active: !rule.is_active });
      await fetchRules();
    } catch (err) {
      console.error('Failed to toggle rule:', err);
    }
  };

  const handleTestRule = async () => {
    if (!editing) return;
    try {
      const res = await apiClient.post(`/api/email-rules/${editing.id}/test_rule/`);
      setTestResult(res.data);
    } catch (err) {
      console.error('Failed to test rule:', err);
    }
  };

  const isEditing = editing || creating;

  const labelStyle: React.CSSProperties = {
    fontSize: TYPOGRAPHY.body.sizes.small,
    fontWeight: TYPOGRAPHY.weight.medium,
    color: COLORS.text.secondary,
    marginBottom: '4px',
    display: 'block',
  };

  const selectStyle: React.CSSProperties = {
    width: '100%',
    padding: '6px 8px',
    fontSize: TYPOGRAPHY.body.sizes.small,
    borderRadius: '4px',
    border: `1px solid ${COLORS.border.default}`,
    backgroundColor: COLORS.bg.panel,
  };

  return (
    <Modal isOpen={isOpen} onClose={() => { resetForm(); onClose(); }} title="Email Rules">
      {!isEditing ? (
        <div>
          <div style={{ marginBottom: SPACING[12] }}>
            <Button variant="primary" size="sm" onClick={openCreate}>Add Rule</Button>
          </div>

          {rules.length === 0 ? (
            <p style={{ color: COLORS.text.secondary, fontSize: TYPOGRAPHY.body.sizes.small }}>
              No rules yet. Create one to auto-process incoming emails.
            </p>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: SPACING[4] }}>
              {rules.map(rule => (
                <div
                  key={rule.id}
                  style={{
                    display: 'flex', alignItems: 'center', gap: SPACING[8],
                    padding: `${SPACING[8]} ${SPACING[12]}`,
                    border: `1px solid ${COLORS.border.default}`,
                    borderRadius: RADIUS,
                    backgroundColor: rule.is_active ? COLORS.bg.panel : COLORS.bg.hover,
                    opacity: rule.is_active ? 1 : 0.6,
                  }}
                >
                  <div style={{ flex: 1 }}>
                    <div style={{ fontWeight: TYPOGRAPHY.weight.medium, fontSize: TYPOGRAPHY.body.sizes.default, color: COLORS.text.primary }}>
                      {rule.name}
                    </div>
                    <div style={{ fontSize: TYPOGRAPHY.body.sizes.tiny, color: COLORS.text.tertiary }}>
                      Priority: {rule.priority} | {Object.entries(rule.conditions).filter(([, v]) => v).map(([k]) => k).join(', ') || 'no conditions'}
                    </div>
                  </div>
                  <button
                    onClick={() => handleToggleActive(rule)}
                    style={{
                      fontSize: TYPOGRAPHY.body.sizes.tiny, padding: '2px 6px', borderRadius: '4px',
                      border: `1px solid ${COLORS.border.default}`, backgroundColor: COLORS.bg.panel, cursor: 'pointer',
                      color: rule.is_active ? COLORS.status.success : COLORS.text.tertiary,
                    }}
                  >
                    {rule.is_active ? 'Active' : 'Inactive'}
                  </button>
                  <button
                    onClick={() => openEdit(rule)}
                    style={{ fontSize: TYPOGRAPHY.body.sizes.tiny, padding: '2px 6px', borderRadius: '4px', border: `1px solid ${COLORS.border.default}`, backgroundColor: COLORS.bg.panel, cursor: 'pointer' }}
                  >
                    Edit
                  </button>
                  <button
                    onClick={() => handleDelete(rule.id)}
                    style={{ fontSize: TYPOGRAPHY.body.sizes.tiny, padding: '2px 6px', borderRadius: '4px', border: `1px solid ${COLORS.border.default}`, backgroundColor: COLORS.bg.panel, cursor: 'pointer', color: COLORS.status.error }}
                  >
                    Delete
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: SPACING[12] }}>
          <div>
            <label style={labelStyle}>Rule Name</label>
            <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Auto-archive GitHub notifications" />
          </div>

          <div>
            <label style={labelStyle}>Priority (higher = runs first)</label>
            <Input type="number" value={priority} onChange={(e) => setPriority(parseInt(e.target.value) || 0)} />
          </div>

          <div style={{ borderTop: `1px solid ${COLORS.border.default}`, paddingTop: SPACING[12] }}>
            <div style={{ fontWeight: TYPOGRAPHY.weight.medium, marginBottom: SPACING[8], color: COLORS.text.primary, fontSize: TYPOGRAPHY.body.sizes.default }}>
              Conditions (AND logic — all non-empty must match)
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: SPACING[8] }}>
              <div>
                <label style={labelStyle}>From contains</label>
                <Input value={conditions.from_contains || ''} onChange={(e) => setConditions(c => ({ ...c, from_contains: e.target.value }))} placeholder="e.g. noreply" />
              </div>
              <div>
                <label style={labelStyle}>From domain (exact)</label>
                <Input value={conditions.from_domain || ''} onChange={(e) => setConditions(c => ({ ...c, from_domain: e.target.value }))} placeholder="e.g. github.com" />
              </div>
              <div>
                <label style={labelStyle}>To contains</label>
                <Input value={conditions.to_contains || ''} onChange={(e) => setConditions(c => ({ ...c, to_contains: e.target.value }))} placeholder="e.g. support@" />
              </div>
              <div>
                <label style={labelStyle}>Subject contains</label>
                <Input value={conditions.subject_contains || ''} onChange={(e) => setConditions(c => ({ ...c, subject_contains: e.target.value }))} placeholder="e.g. [CI]" />
              </div>
            </div>
          </div>

          <div style={{ borderTop: `1px solid ${COLORS.border.default}`, paddingTop: SPACING[12] }}>
            <div style={{ fontWeight: TYPOGRAPHY.weight.medium, marginBottom: SPACING[8], color: COLORS.text.primary, fontSize: TYPOGRAPHY.body.sizes.default }}>
              Actions
            </div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: SPACING[12], marginBottom: SPACING[8] }}>
              <Checkbox id="action-archive" label="Archive" checked={!!actions.archive} onChange={(e) => setActions(a => ({ ...a, archive: e.target.checked }))} />
              <Checkbox id="action-mark_read" label="Mark Read" checked={!!actions.mark_read} onChange={(e) => setActions(a => ({ ...a, mark_read: e.target.checked }))} />
              <Checkbox id="action-star" label="Star" checked={!!actions.star} onChange={(e) => setActions(a => ({ ...a, star: e.target.checked }))} />
              <Checkbox id="action-mark_handled" label="Mark Handled" checked={!!actions.mark_handled} onChange={(e) => setActions(a => ({ ...a, mark_handled: e.target.checked }))} />
              <Checkbox id="action-mark_newsletter" label="Mark Newsletter" checked={!!actions.mark_newsletter} onChange={(e) => setActions(a => ({ ...a, mark_newsletter: e.target.checked }))} />
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: SPACING[8] }}>
              <div>
                <label style={labelStyle}>Assign Category</label>
                <select style={selectStyle} value={actions.assign_category_id || ''} onChange={(e) => setActions(a => ({ ...a, assign_category_id: e.target.value ? parseInt(e.target.value) : null }))}>
                  <option value="">None</option>
                  {categories.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
              </div>
              <div>
                <label style={labelStyle}>Assign Business</label>
                <select style={selectStyle} value={actions.assign_business_id || ''} onChange={(e) => setActions(a => ({ ...a, assign_business_id: e.target.value ? parseInt(e.target.value) : null }))}>
                  <option value="">None</option>
                  {businesses.map(b => <option key={b.id} value={b.id}>{b.name}</option>)}
                </select>
              </div>
              <div>
                <label style={labelStyle}>Assign Project</label>
                <select style={selectStyle} value={actions.assign_project_id || ''} onChange={(e) => setActions(a => ({ ...a, assign_project_id: e.target.value ? parseInt(e.target.value) : null }))}>
                  <option value="">None</option>
                  {projects.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
                </select>
              </div>
              <div>
                <label style={labelStyle}>Assign Person</label>
                <select style={selectStyle} value={actions.assign_person_id || ''} onChange={(e) => setActions(a => ({ ...a, assign_person_id: e.target.value ? parseInt(e.target.value) : null }))}>
                  <option value="">None</option>
                  {people.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
                </select>
              </div>
            </div>
          </div>

          {/* Test result */}
          {testResult && (
            <div style={{ padding: SPACING[8], backgroundColor: COLORS.bg.hover, borderRadius: RADIUS, fontSize: TYPOGRAPHY.body.sizes.small }}>
              <strong>{testResult.match_count}</strong> matches out of {testResult.total_tested} recent emails tested.
              {testResult.samples.length > 0 && (
                <ul style={{ marginTop: SPACING[4], paddingLeft: SPACING[16] }}>
                  {testResult.samples.map((s: any) => (
                    <li key={s.id} style={{ fontSize: TYPOGRAPHY.body.sizes.tiny, color: COLORS.text.secondary }}>
                      {s.subject} — {s.sender}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}

          <div style={{ display: 'flex', gap: SPACING[8], borderTop: `1px solid ${COLORS.border.default}`, paddingTop: SPACING[12] }}>
            <Button variant="primary" onClick={handleSave} disabled={!name.trim() || loading}>
              {editing ? 'Update Rule' : 'Create Rule'}
            </Button>
            {editing && (
              <Button variant="secondary" onClick={handleTestRule}>Test Rule</Button>
            )}
            <Button variant="secondary" onClick={resetForm}>Cancel</Button>
          </div>
        </div>
      )}
    </Modal>
  );
};

export default EmailRuleManager;
